import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckInService, ToastMessageService, BiostartEnrollmentService } from '@fitness-force/api';
import { ClientStatus } from '@fitness-force/enum';
import {
  CheckInDetails,
  ClientById,
  ClientCheckIn,
  ClientCheckInResponse,
  TENANTSINTERFACE,
  TENANT_MODEL,
  BiostartEnrollmentStatusResponse,
  BiostartDevice,
  BiostartScanRequest,
  BiostartScanResponse,
  FaceScanResponse,
  FingerprintScanResponse,
  BiostartEnrollmentSaveRequest,
  CardScanResponse,
} from '@fitness-force/models';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'fitness-force-client-checkin-tab',
  templateUrl: './client-checkin-tab.component.html',
  styleUrls: ['./client-checkin-tab.component.css'],
  standalone: true,
  imports: [    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    BlockUIModule,
    TooltipModule,
    SkeletonModule,
    DatePickerModule,
    DialogModule,
    DropdownModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    DateTimeFormatPipe,
    TranslateModule],
})
export class ClientCheckinTabComponent implements OnInit {
  @Input() clientId: number = 0;
  @Input() client: ClientById | null = null;
  showSkeleton$ = new BehaviorSubject<boolean>(false);
  selectedDates: Date[] = [
    new Date(2024, 4, 1), // March 1, 2023
    new Date(2024, 5, 15), // March 15, 2023
    // Add more preselected dates as needed
  ];
  tableData = new BehaviorSubject<ClientCheckIn[]>([]);
  checkInDetails: CheckInDetails = {} as CheckInDetails;
  checkinParams = new Map();
  pageNumber: number = 1;
  pageSize: number = 999;
  clientCheckInResponse: ClientCheckInResponse = {} as ClientCheckInResponse;
  getAccessTenantList$: Observable<any> | any;
  tenants: TENANT_MODEL[] = [];
  calendarData: Date[][] = [[], [], []]; // Initialize with three empty arrays
  defaultDates: {month:Date, dates:Date[] | null}[] = [];
  loadingTable: boolean = true;
  tenantId: number = 0;
  btnLoading: boolean = false;

  // Biostar Enrollment Properties
  enrollmentStatus: BiostartEnrollmentStatusResponse | null = null;
  availableDevices: BiostartDevice[] = [];
  showEnrollmentDialog: boolean = false;
  selectedEnrollmentType: 'face' | 'fingerprint' | 'card' | null = null;
  selectedDevice: BiostartDevice | null = null;
  isScanning: boolean = false;
  scanResult: BiostartScanResponse | null = null;
  scanPreviewImage: any[] = [];
  scanPreviewData: Array<{image: string, quality?: string}> = [];
  enrollmentLoading: boolean = false;
  enrollmentForm: FormGroup;
  availableEnrollmentTypes: Array<{value: 'face' | 'fingerprint' | 'card', label: string}> = [];

  constructor(
    public cd: ChangeDetectorRef,
    private checkInService: CheckInService,
    private biostartEnrollmentService: BiostartEnrollmentService,
    public translate: TranslateService,
    private store: Store,
    public toastMessageService: ToastMessageService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.enrollmentForm = this.fb.group({
      device: [null, Validators.required],
      enrollmentType: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.showSkeleton$.next(true);
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenants = tenant.all_tenant;
      this.tenantId = tenant.selected_tenant.id;
    });
    this.checkinParams.set('from_date', {
      paramName: 'from_date',
      paramValue: this.getLastThreeMonthsStartDate().toISOString(),
    });
    this.checkinParams.set('to_date', {
      paramName: 'to_date',
      paramValue: this.getCurrentMonthLastDate().toISOString(),
    });
    this.checkinParams.set('PageNumber', {
      paramName: 'PageNumber',
      paramValue: this.pageNumber,
    });
    this.checkinParams.set('PageSize', {
      paramName: 'PageSize',
      paramValue: this.pageSize,
    });

    this.getClientCheckins();
    this.checkEnrollmentStatus();
  }

  getClientCheckins() {
    this.checkInService
      .getCheckInsByClientId(this.clientId, this.getAllParams)
      .subscribe({
        next: (response: ClientCheckInResponse) => {
          console.log(response);
          this.clientCheckInResponse = response;
          this.tableData.next(response.page && response.page.items ? response.page.items : []);
          this.checkInDetails = response.statistic || {};
          this.groupCheckInDatesByMonth();
          this.loadingTable = false;
          this.showSkeleton$.next(false);
          this.cd.detectChanges();
        },
        error: (error) => {
          this.showSkeleton$.next(false);
          this.loadingTable = false;
          this.translate.get('MIGRATION.ERROR_MSG').subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
        },
      });
  }

  groupCheckInDatesByMonth() {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Generate the last three months
    for (let i = 2; i >= 0; i--) {
      const month = currentMonth - i;
      const year = currentYear - Math.floor((currentMonth - i) / 12); // Adjust year if month < 0
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      const monthDates = [];
      const monthsData: {month:Date, dates:Date[] | null} ={
        month: new Date(year, month, 1),
        dates: null
      }
      // Populate the monthDates array with dates for the month
      for (let d = new Date(year, month, 1); d <= monthEnd; d.setDate(d.getDate() + 1)) {
        monthDates.push(new Date(d));
      }

      // Filter dates for which there are events (if any)
      let eventDates: any = [];
      if (
        this.clientCheckInResponse.page &&
        this.clientCheckInResponse.page.items
      ) {
        eventDates = this.clientCheckInResponse.page.items
          .filter((record) => {
            const recordDate = new Date(record.checkin_date_time);
            return recordDate >= monthStart && recordDate <= monthEnd;
          })
          .map((record) => new Date(record.checkin_date_time).getDate());
      }

      // If there are no event dates, keep all dates for the month
      if (eventDates.length === 0) {
        this.calendarData[i] = monthDates;
      } else {
        // Otherwise, filter the monthDates array to include only dates with events
        this.calendarData[i] = monthDates.filter((date) =>
          eventDates.includes(date.getDate())
        );
        monthsData.dates= monthDates.filter((date) =>eventDates.includes(date.getDate()))
      }
      this.calendarData.sort((a, b) => {
        // Extract the month and year from the first date in each array
        const firstDateA = new Date(a[0]);
        const firstDateB = new Date(b[0]);
        const monthA = firstDateA.getMonth();
        const monthB = firstDateB.getMonth();
        const yearA = firstDateA.getFullYear();
        const yearB = firstDateB.getFullYear();

        // Compare years first
        if (yearA !== yearB) {
          return yearA - yearB;
        }
        // If years are the same, compare months
        return monthA - monthB;
      });
      this.defaultDates.push(monthsData);
    }
  }



  markCheckedIn() {
    this.btnLoading = true;
    const payload = { event_type: "CheckIn", access_status_reason: "", machine_id: "" };
    this.checkInService.markClientCheckedIn(this.tenantId, this.clientId, payload).subscribe(
      {
        next: (res: any) => {
          if (res && res.id) {
            if (res.access_status && res.access_status.toLowerCase() == 'denied') {
              this.translate.get('ADMIN.CHECK_IN.CHECK_IN_DENIED')
                .subscribe((translation) => {
                  this.toastMessageService.showErrorMessage(translation);
                });
                this.btnLoading = false;
            }
            else {
              if (payload.event_type === "CheckIn") {
                this.translate
                  .get([
                    'ADMIN.CHECK_IN.CHECK_IN_SUCCESS',
                    'ADMIN.CHECK_IN.CHECK_IN_SUCCESS_MESSAGE',
                  ])
                  .subscribe((translations) => {
                    this.toastMessageService.showCustomSuccess(translations['ADMIN.CHECK_IN.CHECK_IN_SUCCESS'], translations['ADMIN.CHECK_IN.CHECK_IN_SUCCESS_MESSAGE']);
                  });
                  this.btnLoading = false;
              } else {
                this.translate
                  .get([
                    'ADMIN.CHECK_IN.CHECK_OUT_SUCCESS',
                    'ADMIN.CHECK_IN.CHECK_OUT_SUCCESS_MESSAGE',
                  ])
                  .subscribe((translations) => {
                    this.toastMessageService.showCustomSuccess(translations['ADMIN.CHECK_IN.CHECK_OUT_SUCCESS'], translations['ADMIN.CHECK_IN.CHECK_OUT_SUCCESS_MESSAGE']);
                  });
                  this.btnLoading = false;
              };
              this.getClientCheckins();
              this.cd.detectChanges();
            }

          }
        }, error: (error: any) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      }
    )
  }

  get showSkeleton() {
    return this.showSkeleton$.value;
  }

  get getAllParams(): Array<any> {
    return [...this.checkinParams.values()];
  }

  public get clientStatus() {
    return ClientStatus;
  }

  checkedInTenant(tenantId: number) {
    return this.tenants.find((o) => o.id == tenantId)!;
  }

  getLastThreeMonthsStartDate(): Date {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Calculate the month and year for the last three months
    let lastThreeMonthsMonth = currentMonth - 2; // Subtract 2 for the last three months
    let lastThreeMonthsYear = currentYear;

    // Adjust year if needed
    if (lastThreeMonthsMonth < 0) {
      lastThreeMonthsMonth += 12; // 12 months in a year
      lastThreeMonthsYear -= 1;
    }

    // Set the date to the first day of the calculated month
    const lastThreeMonthsStartDate = new Date(
      lastThreeMonthsYear,
      lastThreeMonthsMonth,
      1
    );

    return lastThreeMonthsStartDate;
  }

  getCurrentMonthLastDate(): Date {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Calculate the last day of the current month
    const lastDate = new Date(currentYear, currentMonth + 1, 0);

    return lastDate;
  }

  // Biostar Enrollment Methods
  
  checkEnrollmentStatus() {
    if (this.tenantId && this.clientId) {
      this.biostartEnrollmentService.getEnrollmentStatus(this.tenantId, this.clientId)
        .subscribe({
          next: (response: BiostartEnrollmentStatusResponse) => {
            this.enrollmentStatus = response;
            this.cd.detectChanges();
          },
          error: (error) => {
            console.error('Error checking enrollment status:', error);
            // Initialize as not enrolled if error
            this.enrollmentStatus = {
              is_enrolled: false,
              enrolled_credentials: []
            };
            this.cd.detectChanges();
          }
        });
    }
  }

  openEnrollmentDialog() {
    this.showEnrollmentDialog = true;
    this.loadAvailableDevices();
  }

  loadAvailableDevices() {
    if (this.tenantId) {
      this.biostartEnrollmentService.getSupremaDevices(this.tenantId)
        .subscribe({
          next: (response: BiostartDevice[]) => {
            this.availableDevices = response ? response.filter(o => o.status.toLowerCase() == 'active') : [];
            this.cd.detectChanges();
          },
          error: (error) => {
            this.availableDevices = [];
            this.selectedDevice = null;
            console.error('Error loading devices:', error);
            this.translate.get('BIOSTAR.ERROR_LOADING_DEVICES').subscribe((translation) => {
              this.toastMessageService.showErrorMessage(translation);
            });
          }
        });
    }
  }

  onDeviceSelected() {
    const selectedDevice = this.enrollmentForm.get('device')?.value;
    this.selectedDevice = selectedDevice;
    
    if (selectedDevice) {
      // Determine available enrollment types based on device capabilities
      this.availableEnrollmentTypes = this.getAvailableEnrollmentTypes(selectedDevice);
      
      // Reset enrollment type selection
      this.enrollmentForm.get('enrollmentType')?.setValue(null);
      this.selectedEnrollmentType = null;
      this.cd.detectChanges();
    }
  }

  private getAvailableEnrollmentTypes(device: BiostartDevice): Array<{value: 'face' | 'fingerprint' | 'card', label: string}> {
    // You can modify this logic based on device properties like device.status, device.name, or other capabilities
    // For now, we'll determine based on device name patterns or assume all devices support all types
    
    const enrollmentTypes = [];
    
    // Most biostar devices support fingerprint
    if (device.status.toLowerCase() === 'active') { 
      if(device.supported_types && device.supported_types.includes('Fingerprint')) {
        enrollmentTypes.push({ value: 'fingerprint' as const, label: 'Fingerprint' });
      }
      if (device.supported_types && device.supported_types.includes('Face')) {
        enrollmentTypes.push({ value: 'face' as const, label: 'Face' });
      }
      if (device.supported_types && device.supported_types.includes('Card')) {
        enrollmentTypes.push({ value: 'card' as const, label: 'Card' });
      }
    }
    return enrollmentTypes;
  }

  onEnrollmentTypeSelected() {
    const selectedType = this.enrollmentForm.get('enrollmentType')?.value;
    this.selectedEnrollmentType = selectedType;
    this.cd.detectChanges();
  }

  selectEnrollmentType(enrollmentType: 'face' | 'fingerprint' | 'card') {
    this.selectedEnrollmentType = enrollmentType;
    this.enrollmentForm.get('enrollmentType')?.setValue(enrollmentType);
    this.cd.detectChanges();
  }

  scanCredential() {
    if (!this.selectedDevice || !this.selectedEnrollmentType) {
      return;
    }

    this.isScanning = true;
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];

    const scanRequest: BiostartScanRequest = {
      enrolment_type: this.selectedEnrollmentType
    };

    // Add specific options based on enrollment type
    if (this.selectedEnrollmentType === 'face') {
      scanRequest.pose_sensitivity = 9;
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      scanRequest.ScanFingerprintOption = {
        enroll_quality: "80",
        raw_image: true
      };
    }

    this.biostartEnrollmentService.scanCredential(this.tenantId, this.selectedDevice.id, this.clientId, scanRequest)
      .subscribe({
        next: (response: BiostartScanResponse) => {
          this.scanResult = response;
          if(response.isSuccess){
            this.extractPreviewImage(response);
            this.isScanning = false;
          }
          else{
            this.toastMessageService.showError(response.errorMessage);
            this.isScanning = false;
          }
          this.cd.detectChanges();
        },
        error: (error) => {
         this.toastMessageService.showError(error);
         this.isScanning = false;
         this.scanResult = null;
         this.scanPreviewImage = [];
         this.scanPreviewData = [];
         this.cd.detectChanges();
        }
      });
  }

  extractPreviewImage(scanResponse: BiostartScanResponse) {
    this.scanPreviewData = []; // Reset preview data
    
    if (this.selectedEnrollmentType === 'face') {
      const faceResponse = scanResponse as FaceScanResponse;
      if (faceResponse.data?.credentials?.faces?.[0]?.raw_image) {
        this.scanPreviewImage.push(`data:image/jpeg;base64,${faceResponse.data.credentials.faces[0].raw_image}`);
        this.scanPreviewData.push({
          image: `data:image/jpeg;base64,${faceResponse.data.credentials.faces[0].raw_image}`
        });
      }
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      const fingerprintResponse = scanResponse as FingerprintScanResponse;
      if (fingerprintResponse.data?.FingerprintTemplates) {
        fingerprintResponse.data.FingerprintTemplates.forEach((item: any) => {
          const imageData = `data:image/jpeg;base64,${item.template_image}`;
          this.scanPreviewImage.push(imageData);
          this.scanPreviewData.push({
            image: imageData,
            quality: item.enroll_quality || 'N/A'
          });
        });
      }
      if(fingerprintResponse.data.HasExistingFingerprints){
        fingerprintResponse.data.ExistingUsers.forEach((user: any) => {
          this.toastMessageService.showWarning(`${user.name} already enrolled with ${user.access_groups.map((group: any) => group.name).join(', ')}`,6000);
        });
      }
    }
  }

  saveEnrollment() {
    if (!this.scanResult || !this.selectedEnrollmentType) {
      return;
    }

    this.enrollmentLoading = true;
    const saveRequest: BiostartEnrollmentSaveRequest = {};

    if (this.selectedEnrollmentType === 'face') {
      const faceResponse = this.scanResult as FaceScanResponse;
      if (faceResponse.data?.credentials?.faces?.[0]) {
        saveRequest.faces = [{
          raw_image: faceResponse.data.credentials.faces[0].raw_image,
          templates: faceResponse.data.credentials.faces[0].templates,
          flag: faceResponse.data.credentials.faces[0].flag,
          useProfile: false,
          index: 0
        }];
      }
    } else if (this.selectedEnrollmentType === 'fingerprint') {
      const fingerprintResponse = this.scanResult as FingerprintScanResponse;
      if (fingerprintResponse.data?.FingerprintTemplates) {
        saveRequest.fingerprint_templates = [{
          template0: fingerprintResponse.data.FingerprintTemplates[0].template,
          template1: fingerprintResponse.data.FingerprintTemplates[1].template || '', // Optional template1
          finger_mask: true,
          isNew: true
        }];
      }
    } else if (this.selectedEnrollmentType === 'card') {
      const cardResponse = this.scanResult as CardScanResponse;
      if (cardResponse.data?.SmartCard?.card_id) {
        saveRequest.cards = [{
          card_type: cardResponse.data.SmartCard.card_id.card_type,
          card_id: cardResponse.data.SmartCard.card_id.card_id,
          display_card_id: cardResponse.data.SmartCard.card_id.display_card_id,
          id: cardResponse.data.SmartCard.card_id.card_id // Using card_id as id
        }];
      }
    }

    // Only send the relevant enrollment type in the payload
    const finalPayload: any = {};
    if (this.selectedEnrollmentType === 'face' && saveRequest.faces) {
      finalPayload.faces = saveRequest.faces;
    } else if (this.selectedEnrollmentType === 'fingerprint' && saveRequest.fingerprint_templates) {
      finalPayload.fingerprint_templates = saveRequest.fingerprint_templates;
    } else if (this.selectedEnrollmentType === 'card' && saveRequest.cards) {
      finalPayload.cards = saveRequest.cards;
    }

    this.biostartEnrollmentService.saveEnrollment(this.tenantId, this.clientId, finalPayload)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.showEnrollmentDialog = false;
          this.resetEnrollmentDialog();
          this.isScanning = false;
          this.scanResult = null;
          this.translate.get('BIOSTAR.ENROLLMENT_SUCCESS').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          this.cd.detectChanges();
          this.checkEnrollmentStatus();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  clearEnrollment(type: 'face' | 'fingerprint' | 'card') {
    if (!this.enrollmentStatus?.is_enrolled) {
      return;
    }

    this.enrollmentLoading = true;

    this.biostartEnrollmentService.clearEnrollment(this.tenantId, this.clientId, type)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.isScanning= false;
          this.scanResult = null;
          this.translate.get('BIOSTAR.ENROLLMENT_CLEARED').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          this.showEnrollmentDialog = false;
          this.resetEnrollmentDialog();
          // Refresh enrollment status
          this.checkEnrollmentStatus();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  clearSpecificEnrollment(credentialType: string) {
    if (!this.enrollmentStatus?.is_enrolled) {
      return;
    }

    this.enrollmentLoading = true;
    let enrollmentType:string= '';
    // Map credential type to enrollment type
    if (credentialType.toLowerCase().includes('fingerprint')) {
      enrollmentType = 'Fingerprint';
    } else if (credentialType.toLowerCase().includes('card')) {
      enrollmentType = 'Card';
    } else if (credentialType.toLowerCase().includes('face')) {
      enrollmentType = 'Face';
    }

    this.biostartEnrollmentService.clearEnrollment(this.tenantId, this.clientId, enrollmentType)
      .subscribe({
        next: () => {
          this.enrollmentLoading = false;
          this.translate.get('BIOSTAR.ENROLLMENT_CLEARED').subscribe((translation) => {
            this.toastMessageService.showCustomSuccess('Success', translation);
          });
          // Refresh enrollment status
          this.enrollmentStatus!.enrolled_credentials = this.enrollmentStatus!.enrolled_credentials!.filter((cred: string) => cred.toUpperCase() !== credentialType.toUpperCase());
          this.checkEnrollmentStatus();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.enrollmentLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      });
  }

  getCredentialIcon(credentialType: string): string {
    const type = credentialType.toLowerCase();
    if (type.includes('fingerprint')) {
      return 'fingerprint';
    } else if (type.includes('face')) {
      return 'user';
    } else if (type.includes('card')) {
      return 'credit-card';
    }
    return 'user-circle'; // default icon
  }

  getCredentialLabel(credentialType: string): string {
    const type = credentialType.toLowerCase();
    if (type.includes('fingerprint')) {
      return 'Fingerprint';
    } else if (type.includes('face')) {
      return 'Face';
    } else if (type.includes('card')) {
      return 'Card';
    }
    return credentialType; // return original if no match
  }

  cancelScan() {
    // Clear scan-related data but keep device and enrollment type selection
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];
    this.isScanning = false;
    this.cd.detectChanges();
  }

  resetEnrollmentDialog() {
    this.selectedEnrollmentType = null;
    this.selectedDevice = null;
    this.scanResult = null;
    this.scanPreviewImage = [];
    this.scanPreviewData = [];
    this.isScanning = false;
    this.availableDevices = [];
    this.availableEnrollmentTypes = [];
    this.enrollmentForm.reset();
  }

  onEnrollmentDialogHide() {
    // Clear all data when dialog is closed
    this.resetEnrollmentDialog();
  }

  get enrollmentStatusText(): string {
    if (!this.enrollmentStatus) {
      return 'Configure';
    }
    return this.enrollmentStatus.is_enrolled ? 'Enrolled' : 'Configure';
  }

  get enrollmentButtonText(): string {
    if (!this.enrollmentStatus) {
      return 'Add Enrollment';
    }
    return this.enrollmentStatus.is_enrolled ? 'Clear' : 'Add Enrollment';
  }

  get scanButtonText(): string {
    if (this.selectedEnrollmentType === 'face' || this.selectedEnrollmentType === 'fingerprint') {
      return 'Scan';
    }
    return 'Scan';
  }

  get scanQuality(): string {
    if (this.selectedEnrollmentType === 'fingerprint' && this.scanResult) {
      const fingerprintResult = this.scanResult as FingerprintScanResponse;
      return fingerprintResult.data?.FingerprintTemplates?.[0]?.enroll_quality || 'N/A';
    }
    return 'N/A';
  }
}